/**
 * Garrison buildings. From the fixed camera only roofs and SOUTH walls are ever seen, so
 * doors, lit windows and signs all go on the south face; east and west walls take the
 * ramp's dark step so the corner between them and the front reads.
 */

import type { Palette } from '../palette';
import type { KitBuilder } from './flora';
import { type BoxColors, MeshBuilder, place } from './MeshBuilder';
import { snowOnGable, snowSlab } from './snow';

/** Timber in the woods and the snow, adobe (the desert's `concrete` ramp) in the sand. */
const wallRamp = (p: Palette): string => (p.biome === 'desert' ? 'concrete' : 'timber');

function walls(p: Palette): BoxColors {
  const ramp = wallRamp(p);
  return { all: p.color(`${ramp}.mid`), left: p.color(`${ramp}.lo`), right: p.color(`${ramp}.lo`) };
}

/** Lamp-lit pane with a sill, on a south wall whose outer face is at `z`. */
function litWindow(mb: MeshBuilder, p: Palette, x: number, y: number, z: number, w = 0.8): void {
  mb.box(w, 0.62, 0.1, p.color('fire.2'), place(x, y, z + 0.02));
  mb.box(w + 0.14, 0.1, 0.14, p.color(`${wallRamp(p)}.lo`), place(x, y - 0.06, z + 0.04));
}

/** Desert roofs are flat adobe with a parapet front and back. */
function flatRoof(mb: MeshBuilder, p: Palette, w: number, d: number, y: number, x = 0): void {
  mb.box(w, 0.28, d, p.color('concrete.hi'), place(x, y, 0));
  mb.box(w, 0.32, 0.28, p.color('concrete.hi'), place(x, y + 0.28, d / 2 - 0.14));
  mb.box(w, 0.32, 0.28, p.color('concrete.hi'), place(x, y + 0.28, -d / 2 + 0.14));
}

/**
 * Guard house: the sentry hut where a uniform can be taken (02 §6.4). A pyramid roof tells
 * it from the gabled hut at a glance, and the red board over the door marks it as theirs.
 */
export const guardHouse: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  mb.box(2.6, 2.0, 2.6, walls(p));
  if (p.biome === 'desert') {
    flatRoof(mb, p, 3.0, 3.0, 2.0);
  } else {
    mb.cylinder(2.12, 0, 1.0, 4, p.color('roof.1'), place(0, 1.95, 0, Math.PI / 4));
    if (p.biome === 'arctic') mb.cylinder(1.45, 0, 0.7, 4, p.color('ground.3'), place(0, 2.3, 0, Math.PI / 4));
  }
  mb.box(0.85, 1.65, 0.1, p.color('ink.1'), place(-0.55, 0, 1.32));
  mb.box(0.85, 0.2, 0.08, p.color('enemyAccent.0'), place(-0.55, 1.7, 1.33));
  litWindow(mb, p, 0.6, 1.0, 1.3, 0.7);
  return mb;
};

/** Barracks: the long hut reinforcements come from. A row of lit windows is its signature. */
export const barracks: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  mb.box(9.4, 2.2, 4.2, walls(p));
  if (p.biome === 'desert') {
    flatRoof(mb, p, 10.0, 4.8, 2.2);
  } else {
    // Ridge east-west with generous eaves, so the camera looks straight at the long slope.
    const roof = { w: 10.0, h: 1.4, d: 4.8, y: 2.1 };
    mb.gable(roof.w, roof.h, roof.d, p.color('roof.1'), place(0, roof.y, 0), p.color(`${wallRamp(p)}.lo`));
    snowOnGable(mb, p, roof, [[-3.2, 3.0, 0.74], [0.4, 2.6, 0.6], [3.5, 2.4, 0.7]]);
    mb.box(0.45, 1.0, 0.45, p.color('rock.lo'), place(-2.4, 2.7, -0.9)); // stove pipes
    mb.box(0.45, 1.0, 0.45, p.color('rock.lo'), place(2.6, 2.7, -0.9));
  }
  mb.box(1.0, 1.75, 0.1, p.color('ink.1'), place(0, 0, 2.12));
  mb.box(1.4, 0.18, 0.5, p.color('concrete.lo'), place(0, 0, 2.38));
  for (const x of [-3.7, -1.9, 1.9, 3.7]) litWindow(mb, p, x, 1.05, 2.1);
  return mb;
};

/**
 * Lattice aerial, 11 m to the tip: tall enough and banded boldly enough to be picked out on
 * the flyover (6 px/m), which is how the player finds mission 2's objective. Only the south
 * face is braced - the others are edge-on or hidden from the fixed camera.
 */
function latticeMast(mb: MeshBuilder, p: Palette, x: number, z: number): void {
  const height = 9;
  const base = 0.5;
  const tip = 0.14;
  const lean = Math.atan((base - tip) / height);
  const halfAt = (y: number): number => base - ((base - tip) * y) / height;
  mb.with(place(x, 0, z), () => {
    mb.box(1.3, 0.25, 1.3, p.color('concrete.lo'));
    for (const sx of [-1, 1]) {
      for (const sz of [-1, 1]) {
        mb.box(0.12, height, 0.12, p.color('metal.lo'), place(sx * base, 0.2, sz * base, 0, 1, -sz * lean, sx * lean));
      }
    }
    const bays = 5;
    for (let i = 1; i <= bays; i++) {
      const y = (height * i) / bays;
      const w = halfAt(y) * 2 + 0.2;
      // Collars alternate red and white like an obstruction-marked mast.
      mb.box(w, 0.14, w, p.color(i % 2 ? 'enemyAccent.0' : 'paper.0'), place(0, y + 0.13, 0));
      const y0 = (height * (i - 1)) / bays;
      const span = halfAt(y0) + halfAt(y);
      const rise = y - y0;
      const mid = (y0 + y) / 2;
      const slant = Math.atan(span / rise) * (i % 2 ? 1 : -1);
      const brace = place(0, mid + 0.2, halfAt(mid), 0, 1, 0, slant);
      mb.box(0.09, Math.hypot(span, rise), 0.09, p.color('metal.mid'), brace, true);
    }
    mb.cylinder(0.05, 0.03, 1.9, 4, p.color('metal.lo'), place(0, height + 0.3, 0));
    mb.box(1.7, 0.08, 0.08, p.color('metal.lo'), place(0, height + 0.8, 0));
    mb.box(1.2, 0.08, 0.08, p.color('metal.lo'), place(0, height + 1.4, 0));
    mb.box(0.2, 0.2, 0.2, p.color('fire.0'), place(0, height + 2.1, 0));
  });
}

/** Communications post (mission 2): radio hut on the west of its footprint, aerial on the east. */
export const commsPost: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  const hutX = -0.65;
  mb.box(3.0, 2.2, 3.0, walls(p), place(hutX, 0, 0));
  if (p.biome === 'desert') {
    flatRoof(mb, p, 3.4, 3.4, 2.2, hutX);
  } else {
    const roof = { w: 3.4, h: 1.0, d: 3.6, y: 2.1 };
    mb.with(place(hutX, 0, 0), () => {
      mb.gable(roof.w, roof.h, roof.d, p.color('roof.1'), place(0, roof.y, 0), p.color(`${wallRamp(p)}.lo`));
      snowOnGable(mb, p, roof, [[-0.1, 2.7, 0.72]]);
    });
  }
  mb.box(0.85, 1.65, 0.1, p.color('ink.1'), place(hutX - 0.65, 0, 1.52));
  litWindow(mb, p, hutX + 0.6, 1.0, 1.5, 0.7);
  mb.box(1.5, 0.08, 0.08, p.color('ink.1'), place(0.95, 2.0, 0)); // feeder cable, hut to mast
  latticeMast(mb, p, 1.65, 0);
  return mb;
};

/**
 * Hostage prison (mission 10): squat concrete with corner piers, barred slits, and a
 * strapped steel door that a carbine will not open. Everything that says "prison" is on the south face.
 */
export const prison: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  const lo = p.color('concrete.lo');
  mb.box(6.0, 2.5, 4.8, { all: p.color('concrete.mid'), left: lo, right: lo, top: lo });
  mb.box(6.4, 0.35, 5.2, { all: p.color('concrete.hi'), top: p.color('concrete.mid') }, place(0, 2.5, 0));
  snowSlab(mb, p, 6.4, 5.2, 2.85);
  for (const sx of [-1, 1]) {
    const pier = { all: lo, front: p.color('concrete.mid') };
    for (const sz of [-1, 1]) mb.box(0.6, 2.5, 0.6, pier, place(sx * 2.9, 0, sz * 2.3));
  }
  // Reinforced door: dark steel in a proud frame, two straps across it.
  mb.box(1.7, 2.1, 0.12, lo, place(0, 0, 2.42));
  mb.box(1.3, 1.9, 0.14, p.color('metal.lo'), place(0, 0, 2.46));
  mb.box(1.36, 0.16, 0.18, p.color('metal.mid'), place(0, 0.5, 2.46));
  mb.box(1.36, 0.16, 0.18, p.color('metal.mid'), place(0, 1.3, 2.46));
  // Barred slits: bars 10 cm wide, the thinnest thing that survives 18 px/m.
  for (const x of [-1.85, 1.85]) {
    mb.box(1.0, 0.34, 0.1, p.color('ink.0'), place(x, 1.7, 2.37));
    for (const dx of [-0.3, 0, 0.3]) mb.box(0.1, 0.34, 0.06, p.color('metal.mid'), place(x + dx, 1.7, 2.42));
  }
  return mb;
};
